/**
 * Based on jira/src/packages/issue/issue-view/src/epics/experience-tracking/field-experience-tracking-epic.tsx
 */

import { useCallback, useMemo } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { FeatureFlagValue } from '@atlassian/jira-feature-flagging-using-meta';
import { isAbortableError } from '@atlassian/jira-fetch/src/utils/is-abortable-error.tsx';
import type { AGGError } from '@atlassian/jira-relay-errors/src/index.tsx';
import {
	useAnalyticsSource,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	sendExperienceAnalytics,
	type ExperienceDescription,
} from '@atlassian/jira-issue-view-analytics/src/controllers/send-experience-analytics/index.tsx';
import {
	useApplication,
	useEdition,
	useProjectKey,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import type { Action } from '@atlassian/jira-experience-tracker/src/common/constants.tsx';

type GetEditEventPayloadArg = {
	hasSuccess: boolean;
	hasEdit: boolean;
	errorMessage?: string;
	traceId?: string;
	statusCode?: number;
	action?: Action;
};

/**
 * This hook is used to help with tracking the experience/SLOs of editing a field.
 * We only want to do this for critical fields. See https://hello.atlassian.net/wiki/spaces/JIE/pages/3166048849/Analytics+events+for+relay-powered+Issue+View+fields
 */
export const useFieldExperienceTracking = ({
	fieldId,
}: {
	fieldId: string;
}): {
	sendSubmitSucceededExperienceEvent: () => void;
	sendSubmitFailedExperienceEvent: (error?: Error) => void;
	sendEditCancelExperienceEvent: () => void;
} => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const application = useApplication(projectKey, true);
	const edition = useEdition(projectKey, true);
	const analyticsSource = useAnalyticsSource();

	const getEditEventPayload = useCallback(
		({
			hasSuccess,
			hasEdit,
			errorMessage,
			traceId,
			statusCode,
			action,
		}: GetEditEventPayloadArg): ExperienceDescription => {
			const additionalAttributes: Record<
				string,
				string | boolean | number | Record<string, FeatureFlagValue>
			> = {};
			if (errorMessage != null) {
				additionalAttributes.errorMessage = errorMessage;
			}

			if (traceId != null) {
				additionalAttributes.traceId = traceId;
			}

			if (statusCode) {
				additionalAttributes.statusCode = statusCode;
			}

			return {
				experience: 'editIssue',
				analyticsSource: analyticsSource || 'unknown',
				application: application || null,
				edition: edition || null,
				wasExperienceSuccesful: hasSuccess,
				field: fieldId,
				hasEdit,
				additionalAttributes,
				action,
			};
		},
		[analyticsSource, application, edition, fieldId],
	);

	const sendSubmitSucceededExperienceEvent = useCallback(() => {
		sendExperienceAnalytics(
			getEditEventPayload({
				hasSuccess: true,
				hasEdit: true,
			}),
		);
	}, [getEditEventPayload]);

	const sendSubmitFailedExperienceEvent = useCallback(
		(error?: Error) => {
			if (!error) {
				return;
			}
			const match = error?.message?.match(/response: (\d+)/);
			const statusCode = match ? parseInt(match[1], 10) : undefined;
			if (statusCode && statusCode >= 400 && statusCode < 500) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'issue.issue-view.editIssue',
						teamName: 'obsidian',
					},
					attributes: {
						experience: 'editIssue',
						analyticsSource: analyticsSource || 'unknown',
						application: application || null,
						edition: edition || null,
						wasExperienceSuccesful: false,
						field: fieldId,
						hasEdit: true,
						traceId: 'traceId' in error ? String(error?.traceId) : undefined,
					},
				});
			}
			const action = isAbortableError(error) ? 'taskAbort' : 'taskFail';

			sendExperienceAnalytics(
				getEditEventPayload({
					hasSuccess: isAbortableError(error),
					hasEdit: true,
					errorMessage:
						'statusCode' in error ? `${error.statusCode} - ${error.message}` : error.message,
					traceId: 'traceId' in error ? String(error?.traceId) : undefined,
					statusCode:
						'statusCode' in error && typeof error?.statusCode === 'number'
							? error.statusCode
							: undefined,
					action,
				}),
			);
		},
		[getEditEventPayload, analyticsSource, application, edition, fieldId],
	);

	const sendEditCancelExperienceEvent = useCallback(() => {
		sendExperienceAnalytics(
			getEditEventPayload({
				hasSuccess: true,
				hasEdit: false,
			}),
		);
	}, [getEditEventPayload]);

	return useMemo(
		() => ({
			sendSubmitSucceededExperienceEvent,
			sendSubmitFailedExperienceEvent,
			sendEditCancelExperienceEvent,
		}),
		[
			sendEditCancelExperienceEvent,
			sendSubmitFailedExperienceEvent,
			sendSubmitSucceededExperienceEvent,
		],
	);
};

export const extractFirstErrorFromRelayError = (
	error: Error,
): {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	statusCode: any;
	message: string | undefined;
} | null => {
	if (!('source' in error)) {
		return null;
	}

	const errorSource = error.source;
	const errors =
		typeof errorSource === 'object' &&
		errorSource != null &&
		'errors' in errorSource &&
		errorSource.errors;
	const firstError: AGGError | null = Array.isArray(errors) ? errors[0] : null;

	const statusCode = firstError?.extensions?.statusCode;
	const message = firstError?.message;
	return statusCode ? { statusCode, message } : null;
};
